import api from './api';

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false';
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Mock store — a few seed notifications + helpers so the bell shows
// realistic data without a backend. Survives navigation within a session.
let mockId = 1;
let mockStore = [
  {
    id: mockId++,
    type: 'social_message',
    title: 'Sarah Ahmed sent a message',
    message: 'Hi! I love your last reel. Do you offer collaboration packages?',
    data: {
      platform: 'instagram',
      event_type: 'message',
      actor_name: 'Sarah Ahmed',
      external_event_id: 'demo-ig-msg-1',
    },
    link: '/accounts',
    read_at: null,
    is_read: false,
    created_at: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
  },
  {
    id: mockId++,
    type: 'social_comment',
    title: 'Omar Khaled commented on a post',
    message: 'Great tips! Can you share a follow-up post about scheduling strategy?',
    data: {
      platform: 'facebook',
      event_type: 'comment',
      actor_name: 'Omar Khaled',
      external_event_id: 'demo-fb-cmt-1',
    },
    link: '/posts',
    read_at: null,
    is_read: false,
    created_at: new Date(Date.now() - 34 * 60 * 1000).toISOString(),
  },
  {
    id: mockId++,
    type: 'social_comment',
    title: 'Lina M. commented on a post',
    message: 'This was super helpful. What tool do you use for content planning?',
    data: {
      platform: 'linkedin',
      event_type: 'comment',
      actor_name: 'Lina M.',
      external_event_id: 'demo-li-cmt-1',
    },
    link: '/posts',
    read_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    is_read: true,
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: mockId++,
    type: 'publish_failure',
    title: 'Post failed to publish',
    message: '"Kicking off our spring product launch!" — check the Failure Center for details.',
    data: null,
    link: '/failures',
    read_at: null,
    is_read: false,
    created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
  },
  {
    id: mockId++,
    type: 'token_expired',
    title: 'LinkedIn token expired',
    message: "We couldn't refresh your LinkedIn token. Reconnect the account to resume publishing.",
    data: { platform: 'linkedin' },
    link: '/accounts',
    read_at: null,
    is_read: false,
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: mockId++,
    type: 'publish_success',
    title: 'Post published',
    message: 'Your post was published to 2 accounts.',
    data: null,
    link: '/posts',
    read_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    is_read: true,
    created_at: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
  },
];

export const notificationService = {
  async list({ unreadOnly = false, type, platform } = {}) {
    if (USE_MOCK) {
      await delay(150);
      const sorted = [...mockStore].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      return sorted.filter((n) => {
        if (unreadOnly && n.is_read) return false;
        if (type && n.type !== type) return false;
        if (platform && n?.data?.platform !== platform) return false;
        return true;
      });
    }
    const params = {};
    if (unreadOnly) params.unread_only = true;
    if (type) params.type = type;
    if (platform) params.platform = platform;

    const { data } = await api.get('/notifications', {
      params,
    });
    return Array.isArray(data) ? data : data?.data || [];
  },

  /** GET /api/notifications/unread-count — lightweight polling target. */
  async unreadCount() {
    if (USE_MOCK) {
      await delay(50);
      return { count: mockStore.filter((n) => !n.is_read).length };
    }
    const { data } = await api.get('/notifications/unread-count');
    return data;
  },

  async markRead(id) {
    if (USE_MOCK) {
      await delay(100);
      const n = mockStore.find((x) => x.id === id);
      if (n) {
        n.is_read = true;
        n.read_at = new Date().toISOString();
      }
      return n;
    }
    const { data } = await api.post(`/notifications/${id}/read`);
    return data;
  },

  async markAllRead() {
    if (USE_MOCK) {
      await delay(150);
      const affected = mockStore.filter((n) => !n.is_read).length;
      mockStore.forEach((n) => {
        n.is_read = true;
        n.read_at = n.read_at || new Date().toISOString();
      });
      return { marked: affected };
    }
    const { data } = await api.post('/notifications/read-all');
    return data;
  },

  async remove(id) {
    if (USE_MOCK) {
      await delay(100);
      mockStore = mockStore.filter((x) => x.id !== id);
      return { deleted: true };
    }
    const { data } = await api.delete(`/notifications/${id}`);
    return data;
  },
};
